import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { CalendarDays } from "lucide-react";
import { api } from "@/contexts/AuthContext";

type Props = {
  open: boolean;
  campaign: {
    id: string;
    name?: string;
    created_at?: string;
    google_sheet_name?: string | null;
    template_name?: string | null;
    message_template?: string | null;
    status?: string;
    campaignType?: "marketing" | "renewal" | "dynamic" | "calendar";
    calendar_name?: string | null;
  } | null;
  onClose: () => void;
};

const localStatusConfig: Record<string, { label: string; className: string }> = {
  active: { label: "Activa", className: "bg-green-100 text-green-800 border-green-200" },
  paused: { label: "Pausada", className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  draft: { label: "Borrador", className: "bg-gray-100 text-gray-800 border-gray-200" },
  completed: { label: "Completada", className: "bg-blue-100 text-blue-800 border-blue-200" },
  cancelled: { label: "Cancelada", className: "bg-red-100 text-red-800 border-red-200" },
};

const formatTemplateName = (name: string | null | undefined): string | null => {
  if (!name) return null;
  // Si es un ID numérico largo (template_id de Meta), no mostrarlo como nombre
  if (/^\d{10,}$/.test(name)) return null;
  // Reemplazar guiones bajos por espacios y capitalizar cada palabra
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
};
export default function RenewalDetailsModal({ open, campaign, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [enrichedCampaign, setEnrichedCampaign] = useState<any>(null);
  const [displaySheetName, setDisplaySheetName] = useState<string>("Sin hoja vinculada");

  // --- Hora de envío ---
  const sendingTime = enrichedCampaign?.renewal_config?.sending_time
    ?? enrichedCampaign?.sending_time
    ?? null;

  // Cargar datos completos desde el backend cuando se abre el modal
  useEffect(() => {
    if (!open || !campaign) {
      setEnrichedCampaign(null);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        // Usar endpoint correcto según tipo de campaña
        let endpoint: string;
        if (campaign.campaignType === "marketing") {
          endpoint = `/campaigns/${campaign.id}`;
        } else if (campaign.campaignType === "dynamic") {
          endpoint = `/dynamics/${campaign.id}`;
        } else if (campaign.campaignType === "calendar") {
          endpoint = `/calendar-campaigns/${campaign.id}`;
        } else {
          endpoint = `/renewals/${campaign.id}`;
        }

        const res = await api.get(endpoint);
        const data = res.data;
        const resolvedType = campaign.campaignType || data.type || (endpoint.includes('calendar') ? 'calendar' : endpoint.includes('dynamics') ? 'dynamic' : endpoint.includes('campaigns') ? 'marketing' : 'renewal');
        
        setEnrichedCampaign({
          ...data,
          campaignType: resolvedType
        });
        
        // Resolver nombre de la hoja si no viene
        const sheetId = data.google_sheet_id || data.spreadsheet_id || data.sheet_id;
        if (data.google_sheet_name) {
          setDisplaySheetName(data.google_sheet_name);
        } else if (sheetId) {
          try {
             // Intentar buscar en la lista de hojas del usuario
             const sheetsRes = await api.get("/google-sheets");
             const sheets = Array.isArray(sheetsRes.data) ? sheetsRes.data : sheetsRes.data?.sheets || [];
             const match = sheets.find((s: any) => (s.id === sheetId || s.sheet_id === sheetId || s.spreadsheet_id === sheetId));
             if (match) setDisplaySheetName(match.name || match.title || "Hoja vinculada");
             else setDisplaySheetName("Hoja vinculada");
          } catch {
            setDisplaySheetName("Hoja vinculada");
          }
        } else {
          setDisplaySheetName("Sin hoja vinculada");
        }
        
        console.log('✅ Datos enriquecidos de campaña:', data);
      } catch {
        console.error('❌ Error cargando detalles de campaña');
        setEnrichedCampaign(campaign);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, campaign]);

  if (!open || !campaign) return null;

  const statusCfg = localStatusConfig[enrichedCampaign?.status ?? "draft"] ?? localStatusConfig.draft;

  const displayTemplateNameRaw = enrichedCampaign?.template_name ?? enrichedCampaign?.whatsapp_template_name ?? enrichedCampaign?.meta_template_name ?? null;
  const displayTemplateName = displayTemplateNameRaw ? formatTemplateName(displayTemplateNameRaw) : "Sin nombre de plantilla";

  const periodicityRaw = enrichedCampaign?.periodicity_raw ?? enrichedCampaign?.renewal_config?.periodicity ?? null;
  const reminderDaysArray = enrichedCampaign?.reminder_days
    ?? enrichedCampaign?.renewal_config?.reminder_days_before
    ?? enrichedCampaign?.renewal_config?.reminder_days
    ?? [];


  const modal = (
    <div
      className="fixed inset-0 z-[99999] h-screen w-screen bg-black bg-opacity-60 flex items-start sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="mt-6 sm:mt-0 bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 sm:mx-6 overflow-hidden"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Detalles de la Campaña</h2>
            <p className="text-sm text-gray-600">Información de campaña y plantilla asociada</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} className="text-gray-500 hover:text-gray-700">✖️</Button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 120px)" }}>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
              <span className="text-gray-500">Cargando detalles...</span>
            </div>
          )}

          {!loading && enrichedCampaign && (
            <>
              {/* Resumen narrativo de la campaña con estado */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-[#E8F7F7] to-[#F0FAFA] border border-[#1CB3B3]/20 mb-6">
                {/* Header: Solo Estado */}
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="secondary" className={`${statusCfg.className} border px-3 py-1 rounded-md text-xs font-medium`}>
                    {statusCfg.label}
                  </Badge>
                </div>

                {/* Texto descriptivo */}
                <p className="text-gray-700 leading-relaxed text-sm">
                  {enrichedCampaign.campaignType === "calendar" ? (
                    <>
                      Tu campaña de <span className="font-semibold text-[#0D7377]">Google Calendar</span> <span className="font-semibold text-[#0D7377]">"{enrichedCampaign.name}"</span> está vinculada al calendario <span className="font-semibold text-[#0D7377]">"{campaign?.calendar_name ?? (enrichedCampaign as any).calendar_name ?? "Google Calendar"}"</span>.
                      {(() => {
                        const days = (enrichedCampaign as any).calendar_config?.reminder_days_before || [];
                        const hours = (enrichedCampaign as any).calendar_config?.reminder_hours_before || [];
                        const parts = [];
                        
                        if (days.length > 0) {
                          const sortedDays = [...days].sort((a: number, b: number) => a - b);
                          const dayStr = sortedDays.map(d => `${d} ${Number(d) === 1 ? 'día' : 'días'}`).join(', ');
                          parts.push(dayStr);
                        }
                        if (hours.length > 0) {
                          const sortedHours = [...hours].sort((a: number, b: number) => a - b);
                          const hourStr = sortedHours.map(h => `${h} ${Number(h) === 1 ? 'hora' : 'horas'}`).join(', ');
                          parts.push(hourStr);
                        }
                        
                        if (parts.length > 0) {
                          const reminderText = parts.join(' y ') + ' antes';
                          return (
                            <>
                              {" "}Se enviarán recordatorios a tus clientes <span className="font-semibold text-[#0D7377]">{reminderText}</span> de cada evento programado.
                            </>
                          );
                        }
                        
                        return <> {" "}Se enviarán recordatorios automáticamente a tus clientes según los eventos programados en este calendario.</>;
                      })()}
                    </>
                  ) : enrichedCampaign.campaignType === "dynamic" ? (
                    <>
                      Tu campaña <span className="font-semibold text-[#0D7377]">Dinámica</span> <span className="font-semibold text-[#0D7377]">"{enrichedCampaign.name}"</span> fue creada con la hoja de cálculo <span className="font-semibold text-[#0D7377]">"{displaySheetName}"</span>.
                      {(() => {
                        const config = enrichedCampaign.dynamic_config || {};
                        const dBefore = config.reminder_days_before || [];
                        const hBefore = config.reminder_hours_before || [];
                        const timeCol = enrichedCampaign.time_column || config.time_column;
                        
                        const parts = [];
                        if (dBefore.length > 0) {
                          const sorted = [...dBefore].sort((a,b) => a-b);
                          parts.push(`${sorted.join(', ')} ${sorted[0] === 1 ? 'día' : 'días'}`);
                        }
                        if (hBefore.length > 0) {
                          const sorted = [...hBefore].sort((a,b) => a-b);
                          parts.push(`${sorted.join(', ')} ${sorted[0] === 1 ? 'hora' : 'horas'}`);
                        }

                        if (parts.length > 0) {
                          return (
                            <>
                              {" "}Los mensajes se enviarán <span className="font-semibold text-[#0D7377]">{parts.join(' y ')} antes</span> de la hora indicada en la columna <span className="font-semibold text-[#0D7377]">"{timeCol}"</span>.
                            </>
                          );
                        }
                        return <> {" "}Esta campaña procesa filas dinámicamente según la columna de tiempo <span className="font-semibold text-[#0D7377]">"{timeCol}"</span>.</>;
                      })()}
                    </>
                  ) : (
                    <>
                      Tu campaña de <span className="font-semibold text-[#0D7377]">{enrichedCampaign.campaignType === "marketing" ? "marketing" : "renovación"}</span> <span className="font-semibold text-[#0D7377]">"{enrichedCampaign.name}"</span> fue creada con la hoja de cálculo <span className="font-semibold text-[#0D7377]">"{displaySheetName}"</span>.
                      {periodicityRaw ? (
                        // RENOVACIÓN
                        <>
                          {" "}Cada fila representa un cliente con su propia fecha de renovación.
                          {(() => {
                            const v = Number(periodicityRaw.value) || 1;
                            const unit = String(periodicityRaw.unit || "months").toLowerCase();

                            // Caso especial: diario (1 día) vs cada N días
                            if (unit === "days" || unit === "day") {
                              const daysText = v === 1 ? "todos los días" : `cada ${v} días`;
                              return (
                                <>
                                  {" "}Los mensajes se enviarán <span className="font-semibold text-[#0D7377]">{daysText}</span> a partir de la fecha de renovación
                                  {sendingTime && <>, a las <span className="font-semibold text-[#0D7377]">{sendingTime}</span></>}
                                  .
                                </>
                              );
                            }

                            // Otros casos: semanal, quincenal, mensual, anual
                            let periodicityText = "";
                            if (unit === "weeks" || unit === "week") {
                              periodicityText = v === 1 ? "cada semana" : `cada ${v} semanas`;
                            } else if (unit === "months") {
                              periodicityText = v === 1 ? "cada mes" : `cada ${v} meses`;
                            } else if (unit === "years" || unit === "year") {
                              periodicityText = v === 1 ? "cada año" : `cada ${v} años`;
                            } else {
                              periodicityText = `cada ${v} ${unit}`;
                            }

                            // Formatear días de recordatorio
                            let reminderText = "";
                            if (reminderDaysArray && reminderDaysArray.length > 0) {
                              const sortedDays = [...reminderDaysArray].sort((a: number, b: number) => b - a);
                              if (sortedDays.length === 1) {
                                reminderText = `${sortedDays[0]} ${sortedDays[0] === 1 ? "día antes" : "días antes"}`;
                              } else {
                                const lastDay = sortedDays.pop();
                                reminderText = `${sortedDays.join(", ")} y ${lastDay} días antes`;
                              }
                            }

                            return (
                              <>
                                {" "}Los mensajes se enviarán <span className="font-semibold text-[#0D7377]">{periodicityText}</span>
                                {reminderText && <>, <span className="font-semibold text-[#0D7377]">{reminderText}</span> de cada fecha de renovación</>}
                                {sendingTime && <>, a las <span className="font-semibold text-[#0D7377]">{sendingTime}</span></>}
                                .
                              </>
                            );
                          })()}
                        </>
                      ) : (
                        // MARKETING
                        (() => {
                          // Buscar el campo send_at en diferentes posibles ubicaciones
                          let sendAtRaw = enrichedCampaign.send_at
                            ?? enrichedCampaign.sendAt
                            ?? enrichedCampaign.scheduled_at
                            ?? enrichedCampaign.scheduledAt
                            ?? null;

                          // Si es envío inmediato y ya se activó/envió, usar activated_at si send_at no está disponible
                          if (!sendAtRaw && enrichedCampaign.send_immediately && enrichedCampaign.activated_at) {
                            sendAtRaw = enrichedCampaign.activated_at;
                          }

                          const sendAtDate = sendAtRaw ? new Date(sendAtRaw) : null;
                          const isValidDate = sendAtDate && !isNaN(sendAtDate.getTime());
                          const now = new Date();
                          const isPast = isValidDate && sendAtDate < now;

                          const isImmediate = enrichedCampaign.send_immediately;

                          if (enrichedCampaign.status === "completed" || enrichedCampaign.status === "sent") {
                            // Campaña completada/enviada
                            const sentDate = enrichedCampaign.sent_at || enrichedCampaign.completed_at || sendAtRaw || enrichedCampaign.activated_at || enrichedCampaign.updated_at;
                            return (
                              <>
                                {" "}Los mensajes fueron enviados el <span className="font-semibold text-[#0D7377]">
                                  {sentDate
                                    ? new Date(sentDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
                                    : new Date(enrichedCampaign.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
                                  }
                                </span>
                                {sentDate && (
                                  <> a las <span className="font-semibold text-[#0D7377]">
                                    {new Date(sentDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                  </span></>
                                )}
                                .
                              </>
                            );
                          } else if (isValidDate) {
                            // Campaña programada (activa con fecha futura) o ya pasó la hora
                            return (
                              <>
                                {isPast ? (
                                  <>
                                    {" "}El envío estaba programado para el <span className="font-semibold text-[#0D7377]">
                                      {sendAtDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span> a las <span className="font-semibold text-[#0D7377]">
                                      {sendAtDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                    </span>.
                                  </>
                                ) : (
                                  <>
                                    {" "}Los mensajes se enviarán el <span className="font-semibold text-[#0D7377]">
                                      {sendAtDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span> a las <span className="font-semibold text-[#0D7377]">
                                      {sendAtDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                    </span>.
                                  </>
                                )}
                              </>
                            );
                          } else if (isImmediate) {
                            // Es Inmediata pero aún no tiene fecha (ej. Borrador)
                            return <> Se enviará <span className="font-semibold text-[#0D7377]">inmediatamente</span> tras activar la campaña.</>;
                          } else {
                            // Sin fecha de envío
                            return <> Sin fecha de envío programada.</>;
                          }
                        })()
                      )}
                    </>
                  )}
            </p>
            <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
              <CalendarDays className="w-4 h-4 text-[#1CB3B3]" />
              <span>Creada el {enrichedCampaign.created_at ? new Date(enrichedCampaign.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : "—"}</span>
            </div>
          </div>

          {/* Footer eliminado: sólo queda la X (header) para cerrar el modal */}
        </>
      )}
    </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}